// TablePaymentsModal.jsx
import {DatePicker, Form, InputNumber, message, Modal, Radio} from "antd";
import {useEffect, useState} from "react";
import dayjs from 'dayjs';
import {instance_2} from "../../../../../API/API";

const TablePaymentsModal = ({visible, onClose, onSuccess, payment, objectId, objectName, getIncomePeriodic}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [paymentType, setPaymentType] = useState(1);
    const [costValue, setCostValue] = useState(null);
    const [ndsValue, setNdsValue] = useState(null);

    // Следим за изменением типа платежа
    const handleTypeChange = (e) => {
        const type = e.target.value;
        setPaymentType(type);

        // Если выбран безналичный платеж (type=2) и поле nds пустое или 0, устанавливаем 21%
        if (type === 2) {
            const currentNds = form.getFieldValue('nds');
            if (!currentNds || currentNds === 0 || currentNds === null || currentNds === undefined) {
                form.setFieldsValue({nds: 21});
                setNdsValue(21);
            }
        } else {
            // Для наличных платежей сбрасываем НДС на 0
            form.setFieldsValue({nds: 0});
            setNdsValue(0);
        }
    };

    // Обработчик изменения суммы
    const handleCostChange = (value) => {
        setCostValue(value);
    };

    // Обработчик изменения НДС
    const handleNdsChange = (value) => {
        setNdsValue(value);
    };

    useEffect(() => {
        if (visible && payment) {
            // Режим редактирования
            setPaymentType(payment.payment_type);
            setCostValue(payment.amount);
            setNdsValue(payment.nds || 0);

            form.setFieldsValue({
                cost: payment.amount,
                type: payment.payment_type,
                nds: payment.nds || 0,
                date: dayjs.unix(payment.date)
            });
        } else if (visible) {
            // Режим добавления - устанавливаем текущую дату и тип 1 (наличный)
            setPaymentType(1);
            setCostValue(null);
            setNdsValue(0);

            form.setFieldsValue({
                date: dayjs(),
                type: 1,
                nds: 0
            });
        }
    }, [visible, payment, form]);

    const calculateAmountWithNds = (cost, nds) => {
        // Если сумма не указана, возвращаем 0
        if (!cost || cost <= 0) return 0;
        // Если НДС не указан или равен 0, возвращаем исходную сумму
        if (!nds || nds === 0 || nds === null || nds === undefined) {
            return cost;
        }
        // Иначе возвращаем сумму с НДС
        return cost * (1 + nds / 100);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Получаем значения из формы
            let finalNdsValue = values.nds;

            // Если тип безналичный и НДС не указан (null, undefined, пустая строка), устанавливаем 21%
            if (values.type === 2 && (finalNdsValue === null || finalNdsValue === undefined || finalNdsValue === '')) {
                finalNdsValue = 21;
            }

            // Если тип наличный, принудительно устанавливаем НДС 0
            if (values.type === 1) {
                finalNdsValue = 0;
            }

            // Убеждаемся, что finalNdsValue - число
            finalNdsValue = parseFloat(finalNdsValue) || 0;

            const payload = {
                object_id: objectId,
                cost: values.cost,
                type: values.type,
                nds: finalNdsValue,
                date: values.date.unix()
            };

            let response;
            if (payment) {
                // Редактирование
                response = await instance_2.post('finance/updatePayment.php', {
                    id: payment.originalId,
                    ...payload
                });
            } else {
                // Добавление
                response = await instance_2.post('finance/addPayment.php', payload);
            }

            if (response.data.success) {
                const amountWithNds = calculateAmountWithNds(values.cost, finalNdsValue);

                let messageText = payment ? 'Платеж обновлен' : 'Платеж добавлен';
                if (finalNdsValue > 0) {
                    messageText += `. Сумма без НДС: €${values.cost.toFixed(2)}, с НДС: €${amountWithNds.toFixed(2)}`;
                } else {
                    messageText += `. Сумма: €${values.cost.toFixed(2)}`;
                }

                message.success(messageText);

                // Передаем новый баланс в onSuccess
                if (onSuccess) {
                    onSuccess(response.data.new_balance);
                }

                onClose();
            } else {
                message.error(response.data.error || 'Ошибка при сохранении');
            }
        } catch (error) {
            console.error('Error saving payment:', error);
            message.error('Ошибка при сохранении платежа');
        } finally {
            setLoading(false);
            if (getIncomePeriodic) {
                getIncomePeriodic();
            }
        }
    };

    // Вычисляем сумму с НДС для отображения
    const amountWithNds = calculateAmountWithNds(costValue, ndsValue);

    // Определяем, нужно ли показывать предпросмотр
    const showPreview = costValue && costValue > 0 && ndsValue > 0;

    // Формируем текст подписи для поля суммы
    const getCostLabel = () => {
        if (costValue && costValue > 0) {
            if (ndsValue > 0) {
                return `Сумма без НДС (€) | с НДС: €${amountWithNds.toFixed(2)}`;
            }
            return `Сумма (€) | €${costValue.toFixed(2)}`;
        }
        return 'Сумма (€)';
    };

    return (
        <Modal
            title={payment ? `Редактировать платеж | ${objectName}` : `Добавить платеж | ${objectName}`}
            open={visible}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText={payment ? 'Сохранить' : 'Добавить'}
            cancelText="Отмена"
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={(changedValues) => {
                    if (changedValues.type !== undefined) {
                        handleTypeChange({target: {value: changedValues.type}});
                    }
                    if (changedValues.cost !== undefined) {
                        setCostValue(changedValues.cost);
                    }
                    if (changedValues.nds !== undefined) {
                        setNdsValue(changedValues.nds);
                    }
                }}
            >
                <Form.Item
                    name="date"
                    label="Дата платежа"
                    rules={[{required: true, message: 'Выберите дату'}]}
                >
                    <DatePicker
                        style={{width: '100%'}}
                        format="DD.MM.YYYY"
                        placeholder="Выберите дату"
                    />
                </Form.Item>

                <Form.Item
                    name="type"
                    label="Тип платежа"
                    rules={[{required: true, message: 'Выберите тип платежа'}]}
                >
                    <Radio.Group>
                        <Radio value={1}>Наличный</Radio>
                        <Radio value={2}>Безналичный</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    name="cost"
                    label={getCostLabel()}
                    rules={[
                        {required: true, message: 'Введите сумму'},
                        {type: 'number', min: 0.01, message: 'Сумма должна быть больше 0'}
                    ]}
                >
                    <InputNumber
                        style={{width: '100%'}}
                        precision={2}
                        placeholder="Введите сумму"
                        onChange={handleCostChange}
                    />
                </Form.Item>

                <Form.Item
                    name="nds"
                    label="НДС (%)"
                    extra={paymentType === 2 ? "Для безналичных платежей рекомендуется НДС 21%. Оставьте поле пустым или введите 0, если НДС не применяется." : "Для наличных платежей НДС всегда 0%"}
                >
                    <InputNumber
                        style={{width: '100%'}}
                        precision={2}
                        min={0}
                        max={100}
                        placeholder={paymentType === 2 ? "21 (или 0 если без НДС)" : "0"}
                        disabled={paymentType === 1}
                        onChange={handleNdsChange}
                    />
                </Form.Item>

                {showPreview && (
                    <div style={{
                        marginTop: 10,
                        padding: 12,
                        background: '#e6f7ff',
                        border: '1px solid #91d5ff',
                        borderRadius: 4,
                        textAlign: 'right',
                        fontSize: '16px'
                    }}>
                        <strong>Итого к зачислению: </strong>
                        <span style={{color: '#1890ff', fontWeight: 'bold'}}>
                            €{amountWithNds.toFixed(2)}
                        </span>
                        <div style={{fontSize: '12px', color: '#666', marginTop: 4}}>
                            (сумма без НДС: €{costValue.toFixed(2)} + НДС {ndsValue}%)
                        </div>
                    </div>
                )}

                {paymentType === 2 && ndsValue === 0 && costValue > 0 && (
                    <div style={{
                        marginTop: 10,
                        padding: 10,
                        background: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: 4,
                        color: '#d4b106'
                    }}>
                        <small>⚠️ Вы выбрали безналичный платеж с НДС 0%. Убедитесь, что это правильно.</small>
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default TablePaymentsModal;